/**
 * つながりの構造の確認用デモデータ。
 *
 * 実データでは部署の回答者が7名に届かず、判定（赤・黄・緑）が出ないため、
 * 表示と判定を確認できるように作った固定データ。実データが揃ったら不要になる。
 *
 * 【人数ごとの判定基準（DB05 05-基準表・線形補間）】
 *   回答者数   V(黄)に必要な被指名数   VV(赤)に必要な被指名数
 *      7名          5名 (71%)              6名 (86%)
 *      9名          5名 (56%)              6名 (67%)
 *     12名          6名 (50%)              7名 (58%)
 *     16名          6名 (38%)              7名 (44%)
 *     20名          6名 (30%)              7名 (35%)
 *   7名未満は判定しない（偶然と区別できないため観測値のみ）。
 */
import type { ConsultDeptInput } from './buildOrgReportV3';

const person = (dept: string, i: number, name: string) => ({ id: `${dept}-${i}`, name });

/** 名前の並び（実在の人物とは無関係のダミー）。 */
const NAMES = [
  '佐藤',
  '鈴木',
  '高橋',
  '田中',
  '渡辺',
  '伊藤',
  '山本',
  '中村',
  '小林',
  '加藤',
  '吉田',
  '山田',
  '佐々木',
  '山口',
  '松本',
  '井上',
  '木村',
  '林',
  '清水',
  '森',
];

function members(dept: string, n: number) {
  return Array.from({ length: n }, (_, i) => person(dept, i, NAMES[i % NAMES.length]));
}

/** 全員が hub を挙げる形（1人に集中）。 */
function toHub(dept: string, n: number, hubCount: number) {
  const edges: Array<{ from: string; to: string }> = [];
  for (let i = 1; i <= hubCount; i++) edges.push({ from: `${dept}-${i}`, to: `${dept}-0` });
  // 残りは互いにばらばらに1本ずつ（孤立を作らない）
  for (let i = hubCount + 1; i < n; i++) {
    edges.push({ from: `${dept}-${i}`, to: `${dept}-${(i + 1) % n || 1}` });
  }
  return edges;
}

/**
 * 確認用の4部署。
 *  製造部   9名/6名が1人を挙げる → VV（赤・一極集中）
 *  営業部   9名/5名が1人を挙げる → V （黄・集まりぎみ）
 *  総務部   8名/相互指名の3人組あり → △（緑・助け合い）
 *  技術部   6名/相互指名の3人組あり → 判定なし（下限7名のため。下限を下げれば緑）
 *  品質管理部 4名 → 判定なし（観測値のみ・氏名は表示）
 */
export function demoConsultDepts(): ConsultDeptInput[] {
  const seihin = 'seizo';
  const eigyo = 'eigyo';
  const soumu = 'soumu';
  const hinshitsu = 'hinshitsu';

  return [
    // VV（赤）: 9名中6名が佐藤さんを挙げる = 67% ≥ 9名のVV基準67%
    { dept: '製造部', members: members(seihin, 9), edges: toHub(seihin, 9, 6) },
    // V（黄）: 9名中5名が佐藤さんを挙げる = 56% ≥ 9名のV基準56%、VV基準67%には届かない
    { dept: '営業部', members: members(eigyo, 9), edges: toHub(eigyo, 9, 5) },
    // △（緑）: 相互指名の3人組があり、集中はしていない
    {
      dept: '総務部',
      members: members(soumu, 8),
      edges: [
        { from: `${soumu}-0`, to: `${soumu}-1` },
        { from: `${soumu}-1`, to: `${soumu}-0` },
        { from: `${soumu}-1`, to: `${soumu}-2` },
        { from: `${soumu}-2`, to: `${soumu}-1` },
        { from: `${soumu}-0`, to: `${soumu}-2` },
        { from: `${soumu}-2`, to: `${soumu}-0` },
        { from: `${soumu}-3`, to: `${soumu}-4` },
        { from: `${soumu}-4`, to: `${soumu}-3` },
        { from: `${soumu}-2`, to: `${soumu}-3` },
        { from: `${soumu}-5`, to: `${soumu}-6` },
        { from: `${soumu}-6`, to: `${soumu}-7` },
        { from: `${soumu}-7`, to: `${soumu}-5` },
        { from: `${soumu}-4`, to: `${soumu}-5` },
      ],
    },
    // 6名: 現状は判定なし（△の偶然率4.5%。判定下限を6名に下げれば緑になる構成）
    {
      dept: '技術部',
      members: members('gijutsu', 6),
      edges: [
        { from: 'gijutsu-0', to: 'gijutsu-1' },
        { from: 'gijutsu-1', to: 'gijutsu-0' },
        { from: 'gijutsu-1', to: 'gijutsu-2' },
        { from: 'gijutsu-2', to: 'gijutsu-1' },
        { from: 'gijutsu-0', to: 'gijutsu-2' },
        { from: 'gijutsu-2', to: 'gijutsu-0' },
        { from: 'gijutsu-3', to: 'gijutsu-0' },
        { from: 'gijutsu-4', to: 'gijutsu-3' },
        { from: 'gijutsu-3', to: 'gijutsu-4' },
        { from: 'gijutsu-5', to: 'gijutsu-4' },
      ],
    },
    // 判定なし: 7名未満
    {
      dept: '品質管理部',
      members: members(hinshitsu, 4),
      edges: [
        { from: `${hinshitsu}-1`, to: `${hinshitsu}-0` },
        { from: `${hinshitsu}-2`, to: `${hinshitsu}-0` },
        { from: `${hinshitsu}-3`, to: `${hinshitsu}-1` },
      ],
    },
  ];
}

/**
 * 6名の部署が4つのパターンでどう表現されるかを見るためのデモ。
 *
 * 判定は実際のロジック（judgeConsultDeptV3）をそのまま通す。
 * ここで作るのは指名関係だけで、判定・文言・図はすべて本番と同じ経路を使う。
 *
 * 【6名での判定線】
 *  帰無基準は 6名で83%。被指名数に直すと「6人中5人」で、これが唯一の線になる。
 *  少人数では被指名数が離散的で95%点と99%点が重なるため、V（黄）とVV（赤）は
 *  区別できない。5人に挙げられたら一極集中、それ未満なら集中とはみなさない。
 */
export function demoConsultDepts6(): ConsultDeptInput[] {
  const d = (dept: string, edges: Array<[number, number]>): ConsultDeptInput => ({
    dept,
    members: members(dept.replace(/[^A-Za-z]/g, '') || dept, 6),
    edges: edges.map(([f, t]) => ({
      from: `${dept.replace(/[^A-Za-z]/g, '') || dept}-${f}`,
      to: `${dept.replace(/[^A-Za-z]/g, '') || dept}-${t}`,
    })),
  });

  return [
    // ① 三角のみ: 0-1-2 が相互に挙げ合う。最多被指名は3名（50%）で集中の線に届かない
    d('A_三角のみ', [
      [0, 1],
      [1, 0],
      [1, 2],
      [2, 1],
      [0, 2],
      [2, 0],
      [3, 1],
      [4, 2],
      [5, 3],
      [3, 5],
    ]),
    // ② 三角とV字混在: 0-1-2 が相互に挙げ合い、かつ 5名全員が 0 を挙げる（83%）
    //    集中を優先して表示する（05-RULE-三角）
    d('B_三角とV字', [
      [1, 0],
      [2, 0],
      [3, 0],
      [4, 0],
      [5, 0],
      [0, 1],
      [0, 2],
      [1, 2],
      [2, 1],
    ]),
    // ③ V字のみ: 5名全員が 0 を挙げるが、相互に挙げ合う3人組は無い
    d('C_V字のみ', [
      [1, 0],
      [2, 0],
      [3, 0],
      [4, 0],
      [5, 0],
      [0, 1],
    ]),
    // ④ その他: 一方向にばらけていて、集中も相互の3人組も無い
    d('D_その他', [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 4],
      [4, 5],
      [5, 0],
    ]),
  ];
}
